import { useEffect, useState } from "react";

interface ServiceProgressIndicatorProps {
  serviceCount: number;
}

const ServiceProgressIndicator = ({ serviceCount }: ServiceProgressIndicatorProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDarkBackground, setIsDarkBackground] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('[data-service-section]');
      
      let currentIndex = -1;
      let darkBg = false;
      
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2;
        
        if (isVisible) {
          currentIndex = index;
          // Read the background color from data attribute (reliable method)
          const bgColor = section.getAttribute('data-background');
          
          if (bgColor === 'black') {
            darkBg = true;
          } else if (bgColor === 'white') {
            darkBg = false;
          }
        }
      });
      
      setActiveIndex(currentIndex);
      setIsDarkBackground(darkBg);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const lineColor = isDarkBackground ? 'bg-white' : 'bg-black';
  const lineColorInactive = isDarkBackground ? 'bg-white/20' : 'bg-black/20';
  const textColor = isDarkBackground ? 'text-white' : 'text-black';
  const textColorMuted = isDarkBackground ? 'text-white/60' : 'text-black/60';

  // Only show indicator when viewing service sections (0-3)
  const isVisible = activeIndex >= 0 && activeIndex < serviceCount;

  return (
    <div className={`fixed right-8 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-8 transition-opacity duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}>
      {Array.from({ length: serviceCount }).map((_, index) => (
        <button
          key={index}
          onClick={() => {
            const sections = document.querySelectorAll('[data-service-section]');
            sections[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }}
          className="group relative flex items-center gap-4"
          aria-label={`Ir al servicio ${index + 1}`}
        >
          {/* Line */}
          <div className="relative w-12 h-px overflow-hidden">
            {/* Background line */}
            <div className={`absolute inset-0 ${lineColorInactive} transition-colors duration-300`} />
            {/* Active line */}
            <div 
              className={`absolute inset-0 ${lineColor} origin-left transition-all duration-500 ease-out ${
                activeIndex === index ? 'scale-x-100' : 'scale-x-0'
              }`}
            />
          </div>
          
          {/* Number label - appears on hover */}
          <span 
            className={`text-xs font-light transition-all duration-300 ${
              activeIndex === index 
                ? `opacity-100 ${textColor}` 
                : `opacity-0 group-hover:opacity-60 ${textColorMuted}`
            }`}
          >
            {String(index + 1).padStart(2, '0')}
          </span>
        </button>
      ))}
    </div>
  );
};

export default ServiceProgressIndicator;
